import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, ptBR } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addBusinessDays, format } from 'date-fns';
import { enUS as us, pt } from 'date-fns/locale';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { useIntl } from 'react-intl';

import { TrlFormHelperText } from '»components/atoms/TrlFormHelperText';

import { TrlFieldContainer } from '../TrlFieldContainer';
import { TrlLockedField } from '../TrlLockedField';
import {
  DatePickerStyled,
  FontAwesomeIconStyled,
  FormControlStyled,
} from './TrlDatePicker.styles';

const CalendarIcon = () => <FontAwesomeIconStyled icon={faCalendarDays} />;

export const TrlDatePicker = memo(
  ({
    name,
    label,
    value,
    isDisabled,
    disablePast,
    disableFuture,
    disableWeekends,
    maxBusinessDays,
    dateFormatType,
    isHidden,
    isRequired,
    noPadding,
    isLocked,
    warningText,
  }) => {
    const mobileView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

    const maxDate =
      maxBusinessDays && addBusinessDays(new Date(), maxBusinessDays);

    const intl = useIntl();

    const locale = intl.locale.includes('pt')
      ? {
          text: ptBR,
          adapter: pt,
          dateFormat: 'dd/MM/yyyy',
        }
      : {
          text: enUS,
          adapter: us,
          dateFormat: 'MM/dd/yyyy',
        };

    const dateFormat = 'yyyy-MM-dd';
    const dateTimeFormat = "yyyy-MM-dd'T'00:00:00'Z'";

    const dateFormatted = new Date(value);

    const getDisabledDays = (date) => {
      if (disableWeekends) return date.getDay() === 0 || date.getDay() === 6;
      return false;
    };

    return (
      <Field name={name}>
        {({ form, meta }) => {
          const changeDate = (currentValue) => {
            form.setFieldValue(
              name,
              format(
                currentValue,
                dateFormatType === 'dateTime' ? dateTimeFormat : dateFormat,
              ),
              true,
            );
          };

          const getError = !!(meta.touched && meta.error);

          const hasTouched = () =>
            form.setTouched({ ...form.touched, [name]: true });

          return (
            <FormControlStyled
              className="datePickerContainer"
              fullWidth
              isHidden={isHidden}
            >
              <TrlFieldContainer
                label={label}
                disabled={isDisabled}
                isRequired={isRequired && !isDisabled && !isLocked}
                noPadding={noPadding}
              >
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  localeText={
                    locale.text.components.MuiLocalizationProvider.defaultProps
                      .localeText
                  }
                  adapterLocale={locale.adapter}
                >
                  {isLocked ? (
                    <TrlLockedField
                      showAlertIcon={!!warningText}
                      value={
                        value && value !== ''
                          ? format(dateFormatted, locale.dateFormat, {
                              locale: locale.adapter,
                            })
                          : undefined
                      }
                    />
                  ) : mobileView ? (
                    <DatePickerStyled
                      className="datePickerFieldContainer"
                      value={value !== '' ? dateFormatted : null}
                      disabled={isDisabled}
                      disablePast={disablePast}
                      disableFuture={disableFuture}
                      maxDate={maxDate}
                      shouldDisableDate={getDisabledDays}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                      slotProps={{
                        actionBar: {
                          actions: [],
                        },
                        popper: {
                          style: {
                            zIndex: 2000,
                          },
                        },
                      }}
                      onChange={changeDate}
                      closeOnSelect
                      error={getError}
                      onOpen={hasTouched}
                    />
                  ) : (
                    <DatePickerStyled
                      className="datePickerFieldContainer"
                      value={value !== '' ? dateFormatted : null}
                      disabled={isDisabled}
                      disablePast={disablePast}
                      disableFuture={disableFuture}
                      maxDate={maxDate}
                      shouldDisableDate={getDisabledDays}
                      slots={{
                        openPickerIcon: CalendarIcon,
                      }}
                      slotProps={{
                        popper: {
                          style: {
                            zIndex: 2000,
                          },
                        },
                        textField: {
                          readOnly: true,
                        },
                      }}
                      onChange={changeDate}
                      error={getError}
                      onOpen={hasTouched}
                    />
                  )}
                  {(!!warningText || (meta.touched && meta.error)) && (
                    <TrlFormHelperText
                      helperType={
                        warningText
                          ? 'warning'
                          : meta.error
                            ? 'error'
                            : 'default'
                      }
                      helperText={warningText || meta.error}
                      marginInline={0}
                    />
                  )}
                </LocalizationProvider>
              </TrlFieldContainer>
            </FormControlStyled>
          );
        }}
      </Field>
    );
  },
);

TrlDatePicker.displayName = 'TrlDatePicker';

TrlDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]).isRequired,
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  disableWeekends: PropTypes.bool,
  maxBusinessDays: PropTypes.number,
  dateFormatType: PropTypes.oneOf(['dateTime', 'date']).isRequired,
  isHidden: PropTypes.bool,
  isRequired: PropTypes.bool,
  noPadding: PropTypes.bool,
  isLocked: PropTypes.bool,
  warningText: PropTypes.string,
};
